import * as React from 'react';
import { GPLACES_API_KEY } from '../../../constants/config';

declare const window: Element & { initMap?: (loaded: boolean) => void; mapsloaded?: boolean };
const GOOGLEMAP_API_LOADED = 'GoogleMapApiLoaded';

/**
 * Hook for programatically loading the Google places API via script tag. This hook ensures that there's only
 * 1 copy of google places API loaded at one time and handles for race conditions where new components are initialized
 * before the script has downloaded but after the script tag was added to the DOM.
 */
export const useGooglePlacesApi = () => {
    const [mapsApiLoaded, setMapsApiLoaded] = React.useState(false);

    React.useEffect(() => {
        const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${GPLACES_API_KEY}&libraries=places&callback=initMap`;
        const scriptTagAdded = document.querySelectorAll(`script[src="${scriptUrl}"]`).length > 0;

        if (!scriptTagAdded) {
            // Add the script tag, which will call window.initMap when done
            window.initMap = () => {
                // Dispatch an even on the window element and update the window state to indicate map is loaded
                // this is useful in the cases where other coppies of this component is mounted before maps API loads
                const event = new Event(GOOGLEMAP_API_LOADED);
                window.dispatchEvent(event);

                window.mapsloaded = true;
                // We need to update our component's state here
                setMapsApiLoaded(true);
            };
            const gmapScriptEl = document.createElement(`script`);
            gmapScriptEl.src = scriptUrl;
            (document.querySelector(`body`) as HTMLElement).insertAdjacentElement(`beforeend`, gmapScriptEl);
        } else {
            if (!window.mapsloaded) {
                // Script tag added previously, but maps hasn't completed loading, in which case subscribe for updates and update
                // the maps state
                const componentMapInit = () => {
                    setMapsApiLoaded(true);
                };
                window.addEventListener(GOOGLEMAP_API_LOADED, componentMapInit);
                return () => {
                    window.removeEventListener(GOOGLEMAP_API_LOADED, componentMapInit);
                };
            } else {
                // Script tag added and maps component loaded
                setMapsApiLoaded(true);
            }
        }

        return;
    }, []);

    return mapsApiLoaded;
};
